import { useEffect, useState } from 'react';
import { applyThemePreset, applyNavStyle, DEFAULT_THEME_PRESET, DEFAULT_NAV_STYLE } from '../../theme/presets';

// useSettings reads this synchronously on mount as its anti-flash mechanism.
// Nothing cleared it before, so a saved setting never reached an already-open
// session until something else happened to overwrite the cache.
const PUBLIC_SETTINGS_CACHE_KEY = 'app_settings_cache';

export const useAdminSettings = (activeTab) => {
  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  const fetchSettings = async () => {
    try {
      setSettingsLoading(true);
      const adminToken = localStorage.getItem('admin_token');
      const resp = await fetch(`${API_BASE_URL}/api/admin/settings`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      const data = await resp.json();
      setSettings(data);
    } catch (e) {
      console.error('Failed to load admin settings', e);
    } finally {
      setSettingsLoading(false);
    }
  };

  const updateSettings = async (payload) => {
    const adminToken = localStorage.getItem('admin_token');
    const resp = await fetch(`${API_BASE_URL}/api/admin/settings`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${adminToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(text);
    }
    const data = await resp.json();
    setSettings(data);

    // Drop the public settings cache so the next useSettings mount refetches
    // instead of serving the pre-save snapshot. This fixes a pre-existing bug
    // affecting every public setting (favicon, feature flags), not just theme.
    try {
      localStorage.removeItem(PUBLIC_SETTINGS_CACHE_KEY);
    } catch (e) {
      console.error('Failed to clear public settings cache', e);
    }

    // Apply the theme immediately so the admin sees the change without a
    // reload. Admin and user share an origin and a document, so this also
    // primes the boot script for the next cold start.
    applyThemePreset(data?.theme_preset || DEFAULT_THEME_PRESET);
    applyNavStyle(data?.nav_style || DEFAULT_NAV_STYLE);

    return data;
  };

  useEffect(() => {
    // Lazy-load on settings/Admin tabs
    if (activeTab === 'settings') fetchSettings();
  }, [activeTab]);

  return { settings, settingsLoading, updateSettings };
};